/* eslint-disable max-len */
import { TableBody, TableHead, TableRow, Icon as MuiIcon } from "@mui/material";
import { useState, useRef, useEffect, useContext } from "react";
import theme from "../styles/theme";
import Icon, { ClickableIcon } from "./Icon";
import TableComponent, {
  StyledBodyCell,
  StyledHeaderCell,
  StyledTableRow,
} from "./Table";
import ConfirmModal from "./ConfirmModal";
import { CompetencyType } from "../types/Competency";
import axios from "axios";
import { VersionContext } from "../context/VersionContext";
import SnackBar from "./SnackBar";
interface Column {
  id:
    | "competency_code"
    | "title"
    | "pillar_title"
    | "subdomain_title"
    | "credits"
    | "required"
    | "action"
    | "identity_code";
  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
}
type AllCompetenciesListType = {
  searchId: string;
  mode: "view" | "edit";
  data: Array<any>;
  onSelect: (data: any) => void;
  onClone?: (data: any) => void;
  fetchParent: () => void;
  navHeight?: string;
};
function AllCompetenciesList({
  navHeight,
  searchId,
  data,
  mode,
  onSelect,
  onClone,
  fetchParent,
}: AllCompetenciesListType) {
  const ref = useRef(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [currentCompetency, setCurrentCompetency] = useState<CompetencyType>();
  const { version } = useContext(VersionContext);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
  const [isErrorOpen, setIsErrorOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [columns, setColumns] = useState<Array<Column>>([
    { id: "competency_code", label: "Competency code", minWidth: 100 },
    { id: "title", label: "Title", minWidth: 200 },
    {
      id: "pillar_title",
      label: "Pillar",
      minWidth: 150,
    },
    {
      id: "subdomain_title",
      label: "Subdomain",
      minWidth: 150,
    },
    {
      id: "identity_code",
      label: "Identity Code",
      minWidth: 50,
    },
    {
      id: "required",
      label: "Required",
      minWidth: 30,
    },
    {
      id: "credits",
      label: "Credits",
      minWidth: 50,
    },
  ]);
  useEffect(() => {
    if (mode && mode === "edit") {
      setColumns([
        ...columns,
        {
          id: "action",
          label: "",
          minWidth: 100,
        },
      ]);
    }
  }, [mode]);
  useEffect(() => {
    scrollTo(ref);
  }, [ref, searchId]);
  const scrollTo = (ref: any) => {
    if (ref && ref.current /* + other conditions */) {
      ref.current.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  };
  const deleteComp = () => {
    axios
      .delete(
        `/competency/delete?competency_id=${currentCompetency?.id}&curriculum_version=${version.version_number}`
      )
      .then((res) => {
        setMessage("Deleted competency successfully!");
        setIsSnackbarOpen(true);
        setIsDeleteOpen(false);
        fetchParent();

        // window.location.reload();
      })
      .catch(function (error) {
        if (error.response) {
          setMessage(
            error.response.data.error || "Failed to delete competency!"
          );
          setIsErrorOpen(true);
        } else if (error.request) {
          console.log(error.request);
        } else {
          setMessage(error.response.data.error);
          setIsErrorOpen(true);
        }
      });
  };
  return (
    <>
      <SnackBar
        isOpen={isSnackbarOpen}
        setOpen={setIsSnackbarOpen}
        message={message}
        type="success"
      />
      <SnackBar
        isOpen={isErrorOpen}
        setOpen={setIsErrorOpen}
        header="Error!"
        message={message}
        type="error"
      />
      <ConfirmModal
        isOpen={isDeleteOpen}
        setOpen={setIsDeleteOpen}
        confirmFunction={() => deleteComp()}
        title={"Remove Competency"}
        description={
          "Are you sure you want to remove this competency from selected version?"
        }
      />
      <TableComponent navHeight={navHeight} aria-label="sticky table">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <StyledHeaderCell
                key={column.id}
                align={column.align}
                backgroundColor={theme.color.lightBlueGray50}
                color={theme.color.lightBlueGray800}
                 style={{ 
                  minWidth: column.minWidth,
                  position: "sticky",
                  top: 0,
                  zIndex: 20,
                  ...(column.id === "action" && {
                    right: 0,
                    zIndex: 21,
                    boxShadow:'-4px 0px 6px #f0f0f099'
                  })
                }}
              >
                {column.label}
              </StyledHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data && data.length > 0 ? (
            data.map((row: any, index) => {
              return (
                <StyledTableRow
                  onDoubleClick={() => onSelect(row)}
                  selected={searchId === row.id}
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  ref={searchId === row.id ? ref : null}
                  key={index}
                >
                  {columns.map((column) => {
                    if (column.id === "action") {
                      return (
                        <StyledBodyCell
                          style={{ verticalAlign: "middle", position: "sticky",   right: 0,background:'white', boxShadow:'-4px 0px 6px #f0f0f099' }}
                          key={column.id}
                          align={"right"}
                        >
                          {!row.derived_from_id&&<span className="reduce_opct_onhover" title="Clone">
                            <ClickableIcon
                              style={{ color: "#00ab00" }}
                              onClick={() => {
                                onClone && onClone(row);
                              }}
                              fixedWidth={false}
                              size={3}
                              name={"file_copy"}
                            />
                          </span>}
                          <span className="reduce_opct_onhover" title="Edit">
                            <ClickableIcon
                              style={{ marginLeft: "10px",color: 'gray' }}
                              onClick={() => {
                                onSelect(row);
                              }}
                              fixedWidth={false}
                              size={3}
                              name={"edit"}
                            />
                          </span>
                          {(
                            <MuiIcon
                              title="Delete"
                              className="reduce_opct_onhover"
                              onClick={() => {
                                setCurrentCompetency(row);
                                setIsDeleteOpen(true);
                              }}
                              sx={{
                                color: "red",
                                marginLeft: "10px",
                                // textShadow: "0 2px 4px #8080807a",
                              }}
                            >
                              delete_outline_icon
                            </MuiIcon>
                          )}
                        </StyledBodyCell>
                      );
                    }
                    const value = row[column.id];
                    return (
                      <StyledBodyCell key={column.id} align={column.align}>
                        {column.format && typeof value === "number" ? (
                          column.format(value)
                        ) : value === true ? (
                          <>
                            <MuiIcon
                              sx={{
                                color: "green",
                                marginLeft: "10px",
                                // textShadow: "0 2px 4px #8080807a",
                              }}
                            >
                              check_icon
                            </MuiIcon>
                            {row.option_group && (
                              <MuiIcon
                                sx={{
                                  color: "#ffbb00",
                                  marginLeft: "10px",
                                  // textShadow: "0 2px 4px #8080807a",
                                }}
                              >
                                star_icon
                              </MuiIcon>
                            )}
                          </>
                        ) : value === false ? (
                          <>
                            <MuiIcon
                              className="reduce_opct_onhover"
                              sx={{
                                color: "red",
                                marginLeft: "10px",
                                // textShadow: "0 2px 4px #8080807a",
                              }}
                            >
                              clear_icon
                            </MuiIcon>
                            {row.option_group && (
                              <MuiIcon
                                sx={{
                                  color: "#ffbb00",
                                  marginLeft: "10px",
                                  // textShadow: "0 2px 4px #8080807a",
                                }}
                              >
                                star_icon
                              </MuiIcon>
                            )}
                          </>
                        ) : (
                          value
                        )}
                      </StyledBodyCell>
                    );
                  })}
                </StyledTableRow>
              );
            })
          ) : (
            <StyledTableRow
              hover
              role="checkbox"
              tabIndex={-1}
              // onClick={() => {
              //   onSelect(row);
              // }}
              key={0}
            >
              <StyledBodyCell key={1} align={"center"} colSpan={5}>
                No competency for the selected version
              </StyledBodyCell>
            </StyledTableRow>
          )}
        </TableBody>
      </TableComponent>
    </>
  );
}
export default AllCompetenciesList;
